/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

function DeliveryProfilesEmailsSearchViewController(
    this: JS5Object,
    _: JS5Object,
    $location: JS5Object,
    $q: JS5Object,
    $routeParams: JS5Object,
    uiGridConstants: JS5Object,
    BreadcrumbService: JS5Object,
    NotificationService: JS5Object,
    PartnerEditService: JS5Object,
    PartnerResourceFactory: JS5Object,
    PermissionsService: JS5Object
) {
    const SYSTEM_PARTNERS_DELIVERYPROFILES_VIEW_STATE = 'SYSTEM_PARTNERS_DELIVERYPROFILES_VIEW_STATE';

    //=========//
    // Routing //
    //=========//

    const setupBreadcrumbs = () => {
        BreadcrumbService.clearBreadcrumbs();
        BreadcrumbService.addBreadcrumb({
            name: 'Partners',
            "location": "/system/partners/search",
            "params": {
                "tab": "properties"
            },
        });
        BreadcrumbService.addBreadcrumb({
            name: this.partnerName,
            location: `/system/partners/edit/${$routeParams.id}/deliveryProfiles`
        });
    };

    const navigateToDeliveryEmailEditView = (deliveryEmail: JS5Object): void => {
        if (this.writePerms) {
            if (deliveryEmail.hidden) {
                NotificationService.createWarningNotification(`"${deliveryEmail.emailConfig.name}" is hidden`);
            } else {
                $location.url(`/system/partners/edit/${$routeParams.id}/deliveryProfiles/edit/${$routeParams.deliveryProfileId}/emails/edit/${deliveryEmail.id}`);
            }
        }
    };


    //======//
    // Grid //
    //======//

    const columnDefinitions: JS5Object[] = [
        {
            field: 'emailConfig.name',
            displayName: 'Name',
            cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
        },
        {
            field: 'emailConfig.emailTemplate.name',
            displayName: 'Email Template',
            cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
        },
        {
            field: 'emailConfig.recipients.length',
            displayName: '# of Recipients',
            cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
        }
    ];

    this.gridOptions = {
        rowSelection: true,
        enableRowHeaderSelection: false,
        enableSelectAll: false,
        enableColumnResizing: true,
        enablePaginationControls: false,
        enableSorting: true,
        enableColumnMenus: false,
        enableFullRowSelection: true,
        enableHorizontalScrollbar: uiGridConstants.ALWAYS,
        enableVerticalScrollbar: uiGridConstants.ALWAYS,
        rowHeight: 26,
        multiSelect: false,
        columnDefs: columnDefinitions,
        onRegisterApi: (gridApi: JS5Object): void => {
            this.gridApi = gridApi;
        },
        rowTemplate: 'components/visual/grid/searchview-component-row-template.html',
        data: []
    };

    this.onRowDblClick = (row: JS5Object) => {
        navigateToDeliveryEmailEditView(row.entity);
    };

    const getData = () => {
        let promises = [];
        this.showHidden = $location.search().showHidden;
        this.loadingIndicator.show = true;

        promises.push(PartnerResourceFactory.get({id: $routeParams.id}).$promise
            .then((partner: JS5Object) => {
                this.partnerName = partner.name;
            }));
        if (this.readPerms) {
            promises.push(PartnerEditService.getDeliveryProfile($routeParams.deliveryProfileId, this.showHidden)
            .then((deliveryProfile: JS5Object) => {
                this.gridOptions.data = deliveryProfile.deliveryEmailConfigs;
            }));
        }

        return $q.all(promises).finally(() => {
            this.loadingIndicator.show = false;
        });
    };


    //==================//
    // HTML Interfacing //
    //==================//

    const getSelectedEmail = () => {
        return _.first(this.gridApi.selection.getSelectedRows());
    };

    const onNewClicked = () => {
        navigateToDeliveryEmailEditView({id: -1});
    };

    const onEditClicked = () => {
        navigateToDeliveryEmailEditView(getSelectedEmail());
    };

    const onHideClicked = () => {
        let profile = getSelectedEmail();
        if (!profile.hidden) {
            this.loadingIndicator.show = true;
            PartnerEditService.toggleDeliveryEmailHidden($routeParams.deliveryProfileId, profile.id).then(getData);
        }
    };

    const onUnhideClicked = () => {
        let profile = getSelectedEmail();
        if (profile.hidden) {
            this.loadingIndicator.show = true;
            PartnerEditService.toggleDeliveryEmailHidden($routeParams.deliveryProfileId, profile.id).then(getData);
        }
    };

    this.toolbarButtonDefs = [
        {
            label: 'New',
            enabled: () => this.writePerms,
            onClick: onNewClicked
        },
        {
            label: 'Edit',
            enabled: () => this.writePerms && getSelectedEmail(),
            onClick: onEditClicked
        },
        {
            label: 'Hide',
            enabled: () => this.writePerms && getSelectedEmail() && !_.get(getSelectedEmail(), 'hidden'),
            onClick: onHideClicked
        },
        {
            label: 'Unhide',
            enabled: () => this.writePerms && _.get(getSelectedEmail(), 'hidden'),
            onClick: onUnhideClicked
        }
    ];

    this.toolbarCheckboxDefs = [
        {
            label: 'Show Hidden',
            onUpdate: (newState: boolean) => {
                this.showHidden = newState;
                $location.search('showHidden', newState);
                // Changing search should restart the component. In case it doesn't, manually call getData
                getData();
            },
            isChecked: this.showHidden
        }
    ];

    //=================//
    // Lifecycle Hooks //
    //=================//

    const getPermissions = () => {
        this.isAdmin = PermissionsService.isAdmin();

        let permsPromises = {
            canRead: PermissionsService.canRead(SYSTEM_PARTNERS_DELIVERYPROFILES_VIEW_STATE),
            canWrite: PermissionsService.canWrite(SYSTEM_PARTNERS_DELIVERYPROFILES_VIEW_STATE),
        };

        return $q.all(permsPromises).then((perms: JS5Object) => {
            this.readPerms = _.get(perms, 'canRead') || this.isAdmin;
            this.writePerms = _.get(perms, 'canWrite') || this.isAdmin;
        });
    };

    this.$onInit = () => {
        this.showHidden = $location.search().showHidden;
        // Binding doesn't get initial state, so get it here
        this.toolbarCheckboxDefs[0].isChecked = this.showHidden;
        this.loadingIndicator = {
            backdrop: true,
            show: true,
            title: 'Working',
            message: 'Please wait...'
        };

        getPermissions().then(getData).then(setupBreadcrumbs);
    };
}


angular.module('prism-ui.deliveryProfiles').component('deliveryProfilesEmailsSearchView', {
    templateUrl: 'system/partner/editor/components/view/deliveryProfiles/editor/components/emails/searchView/delivery-profiles-emails-search-view-template.html',
    controller: DeliveryProfilesEmailsSearchViewController
});
